import { LitElement, html, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-timeinput/src/voy-timeinput';
import '../../voy-terminal-input/src/voy-terminal-input';

import { VoyTimeinput } from '../../voy-timeinput/src/voy-timeinput';
import { ConvertDateToAnotherTimeZone, GetFormattedDate, GetFormattedDateNoYear } from '../../../src/utils/helpers';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { TerminalInfo } from '../../../src/models/types/terminals-model.types';
import { SimplifiedPortCall } from '../../../src/models/types/voyage-schedules-model.types';

enum ErrorMsgs {
    EtaNotInFuture = "Select an ETA in the future",
    EtdAfterNextPort = "ETD must be before next portcall ETA",
    EtaBeforePreviousPortEtd = "ETA must be after previous port ETD",
    EtaAfterEtd = "ETA must be earlier than ETD",
    EtdBeforeEta = "ETD must be later ETA",
    MissingChangedPortEtd = "Must input ETD",
    MissingChangedPortEta = "Must input ETA",
}

@customElement("vessel-edit-schedule-modify")
export class VesselEditScheduleModify extends LitElement {

    @property({attribute: false}) previousPort: SimplifiedPortCall | null = null;
    @property({attribute: false}) currentPort: SimplifiedPortCall | null = null;
    @property({attribute: false}) nextPort: SimplifiedPortCall | null = null;
    @property({attribute: false}) changedPortId: string = "";
    @property({attribute: false}) changedPortEta: number = -1;
    @property({attribute: false}) changedPortEtd: number = -1;
    @property({attribute: false}) formIsValid: boolean = false;
    @property({type: Boolean}) shown: boolean = false;

    static get styles() {
        return [
            css`
                .container {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    place-content: center;
                    width: 695px;
                    border: 1px solid var(--neutrals-grey-07);
                    background: var(--neutrals-grey-10);
                    font-family: MaerskText;;
                    font-size: 14px;
                }

                .port-call {
                    display: grid;
                    grid-template-rows: auto auto auto auto;
                    place-content: start;
                    grid-gap: 20px;
                    margin: 0 auto;
                    padding-top: 24px;
                    padding-bottom: 24px;
                }

                .current {
                    padding-right: 29px;
                    padding-left: 24px;
                }

                .changed {
                    padding-left: 29px;
                    padding-right: 24px;
                }

                voy-timeinput {
                    height: 68px;
                }

                .error-label {
                    font-size: 12px;
                    margin-top: 5px;
                    display: none;
                    color: var(--feedback-danger, red);
                }

                #arrival-terminal-eta-error-label[show] {
                    display: block;
                }

                #arrival-terminal-etd-error-label[show] {
                    display: block;
                }

                .footer {
                    color: var(--functional-grey-700);
                    font-size: 12px;
                    margin-top: -15px;
                    min-height: 38px;
                }
        `];
    }

    render() {
        return html`
            <voyage-popup
                no-close-button ?shown=${this.shown} header-text=${`Change port`}
                @close=${this._onClose} @accept=${this._onAccept}
                accept-button-text=${"Confirm"} no-overflow
                ?disable-accept-button=${!this.formIsValid}
            >
                <div class="container" data-test="change-port-popup">
                    ${this._renderCurrentPortInputs()}
                    ${this._renderChangedPortInputs()}
                </div>
            </voyage-popup>
       `;
    }

    firstUpdated() {
        this._populateInputs();
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        changedProperties.forEach((_, propName) => {
            switch (propName) {
                case "currentPort":
                    this._populateInputs();
                    break;
                case "changedTerminalId":
                case "changedPortEta":
                case "changedPortEtd":
                    this._validateForm();
                    break;
            }
        })

        super.updated(changedProperties);
    }

    @property({attribute: false}) private _changedPortEtaErrorMsg = "";
    @property({attribute: false}) private _changedPortEtdErrorMsg = "";

    private _renderCurrentPortInputs() {
        return html`
            <div class="port-call current" data-test="from">
                <voy-terminal-input
                    .terminalId=${this.currentPort ? this.currentPort!.terminalCode : ""}
                    .label=${"Port (current)"} .readonly=${true}>
                </voy-terminal-input>
                <voy-timeinput
                    custom-label=${`ETA${this.currentPort!.terminalCode ? " (local time)" : " (UTC)"}`}
                    id="from-eta" terminal-id=${this.currentPort ? this.currentPort!.terminalCode : ""}
                    ?readonly=${true}>
                </voy-timeinput>
                <voy-timeinput
                    custom-label=${`ETD${this.currentPort!.terminalCode ? " (local time)" : " (UTC)"}`}
                    id="from-etd" terminal-id=${this.currentPort ? this.currentPort!.terminalCode : ""}
                    ?readonly=${true}>
                </voy-timeinput>
                <div class="footer"></div>
            </div>
        `
    }

    private _renderChangedPortInputs() {
        return html`
            <div class="port-call changed" data-test="to">
                <div>
                    <voy-terminal-input
                        id="resultingTerminal" .terminalId=${this.changedPortId} .label=${"Port (change to)"} 
                        @terminal-selected=${(evt:CustomEvent<TerminalInfo>) => { this.changedPortId = evt.detail.locationId }}>
                    </voy-terminal-input>
                </div>
                <div>
                    <voy-timeinput
                        custom-label=${`ETA${this.changedPortId ? " (local time)" : " (UTC)"}`} 
                        id="to-eta" terminal-id=${this.changedPortId} .value=${this.changedPortEta}
                        use-blur-event
                        @input=${(evt:InputEvent) => { this.changedPortEta = (<VoyTimeinput>evt.target).value }}
                        .invalid=${this._changedPortEtaErrorMsg.length}>
                    </voy-timeinput>
                    <span
                        class="error-label" id="arrival-terminal-eta-error-label" 
                        ?show=${this._changedPortEtaErrorMsg.length}>${this._changedPortEtaErrorMsg}
                    </span>
                </div>
                <div>
                    <voy-timeinput
                        custom-label=${`ETD${this.changedPortId ? " (local time)" : " (UTC)"}`}
                        id="to-etd" terminal-id=${this.changedPortId} .value=${this.changedPortEtd}
                        use-blur-event
                        @input=${(evt:InputEvent) => { this.changedPortEtd = (<VoyTimeinput>evt.target).value }}
                        .invalid=${this._changedPortEtdErrorMsg.length}>
                    </voy-timeinput>
                    <span
                        class="error-label" id="arrival-terminal-etd-error-label"
                        ?show=${this._changedPortEtdErrorMsg.length}>${this._changedPortEtdErrorMsg}
                    </span>
                </div>
                <div class="footer">If you don’t know the exact ETD, you can add one now and update it later.</div>
            </div>
        `
    }

    private _populateInputs() {
        if (!this.currentPort) {
            return;
        }
        this.changedPortId = this.currentPort.terminalCode;
        (<VoyTimeinput>this.shadowRoot!.getElementById("from-eta")).value = new Date(this.currentPort!.arrivalAt).getTime();    
        (<VoyTimeinput>this.shadowRoot!.getElementById("from-etd")).value = new Date(this.currentPort!.departureAt).getTime();  
        this.changedPortEta = new Date(this.currentPort!.arrivalAt).getTime();
        this.changedPortEtd = new Date(this.currentPort!.departureAt).getTime();
    }

    private _validateForm() {
        this._validateChangedPortEtd();
        this._validateChangedPortEta();
        this.formIsValid = !this._changedPortEtaErrorMsg.length
                        && !this._changedPortEtdErrorMsg.length
                        && this.changedPortId.length > 0;
    }

    private _validateChangedPortEta() {
        // don't show errors before a port is selected
        if (!this.changedPortId) {
            this._changedPortEtaErrorMsg = "";
            return;
        }

        const etdAdded = this.changedPortEta !== -1;
        if (!etdAdded) {
            this._changedPortEtdErrorMsg = ErrorMsgs.MissingChangedPortEtd;
        }

        const isInFuture = this.changedPortEta > Date.now();
        if (!isInFuture) {
            this._changedPortEtaErrorMsg = ErrorMsgs.EtaNotInFuture;
        }

        const isBeforeArrivalPortEtd = this.changedPortEta < this.changedPortEtd;
        if (!isBeforeArrivalPortEtd) {
            this._changedPortEtaErrorMsg = ErrorMsgs.EtaAfterEtd;
        }

        let isAfterPreviousPortEtd = true;
        if (this.previousPort) {
            const previousPortEtd = new Date(this.previousPort.departureAt).getTime();
            isAfterPreviousPortEtd = this.changedPortEta > previousPortEtd;

            if (!isAfterPreviousPortEtd && this.changedPortId && !TerminalsModel!.isPseudoTerminal(this.changedPortId)) {
                const localtime = ConvertDateToAnotherTimeZone(this.previousPort.arrivalAt, TerminalsModel!.getTerminal(this.changedPortId!)!.olsonTimezone!);
                const formattedDate = GetFormattedDateNoYear(localtime).substring(0, 16);
                this._changedPortEtaErrorMsg = `Must be later than ${formattedDate} to leave previous port`
            }
        }

        if (this._changedPortEtaErrorMsg.length && isInFuture && isBeforeArrivalPortEtd && isAfterPreviousPortEtd) {
            this._changedPortEtaErrorMsg = "";
        }
    }

    private _validateChangedPortEtd() {
        // don't show errors before a port is selected
        if (!this.changedPortId) {
            this._changedPortEtdErrorMsg = "";
            return;
        }

        const etdAdded = this.changedPortEtd !== -1;
        if (!etdAdded) {
            this._changedPortEtdErrorMsg = ErrorMsgs.MissingChangedPortEtd;
        }

        const isAfterArrivalPortEta = this.changedPortEta < this.changedPortEtd;
        if (!isAfterArrivalPortEta) {
            this._changedPortEtdErrorMsg = ErrorMsgs.EtdBeforeEta;
        }

        let isBeforeNextPortEta = true;
        if (this.nextPort) {
            const nextPortEta = new Date(this.nextPort.arrivalAt).getTime();
            isBeforeNextPortEta = this.changedPortEtd < nextPortEta;

            if (!isBeforeNextPortEta && this.changedPortId && !TerminalsModel!.isPseudoTerminal(this.changedPortId)) {
                const localtime = ConvertDateToAnotherTimeZone(this.nextPort.arrivalAt, TerminalsModel!.getTerminal(this.changedPortId!)!.olsonTimezone!);
                const formattedDate = GetFormattedDateNoYear(localtime).substring(0, 16);
                this._changedPortEtdErrorMsg = `Must be earlier than ${formattedDate} to reach next port`;
            }
        }

        if (this._changedPortEtdErrorMsg.length && isBeforeNextPortEta && isAfterArrivalPortEta) {
            this._changedPortEtdErrorMsg = "";
        }
    }

    private _onClose(){
        this.dispatchEvent(new Event('cancel'));
    }

    private async _onAccept() {
        this.dispatchEvent(new CustomEvent<SimplifiedPortCall>('accept', { detail: {
            gsisId: this.currentPort!.gsisId,
            localId: this.currentPort!.localId,
            terminalCode: this.changedPortId,
            arrivalAt: new Date(this.changedPortEta).toISOString(),
            departureAt: new Date(this.changedPortEtd).toISOString(),
            omitted: false,
            actuals: { pilotStationArrivalAt: null, pilotOnAt: null, arrivalAt: null, departureAt: null, pilotOffAt: null }
        }}));
    }    
}
